import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTutorial } from './TutorialContext';

const PAD = 8; // gap between the highlight frame and the real element
const FIND_TIMEOUT_MS = 3000;
const FIND_POLL_MS = 100;

// The actual on-screen tour overlay — mounted once at the top of App.js
// (inside TutorialProvider, outside <Routes>) so it persists across the
// navigation the tour itself triggers. Renders nothing when no tour is
// active (components/tutorial/TutorialContext.jsx owns all the state).
export default function TutorialSpotlight() {
    const { active, currentStep, currentIndex, steps, next, back, endTour, isFirst, isLast } = useTutorial();
    const [rect, setRect] = useState(null);
    const [searching, setSearching] = useState(true);
    const containerRef = useRef(null);

    // Re-resolve the target element every time the step changes — it may
    // live on a page that just navigated to and hasn't finished rendering
    // yet (e.g. a dashboard still fetching data), so this polls rather than
    // checking once. If it never appears, falls back to a centered card
    // with no highlight rather than leaving the tour stuck.
    useEffect(() => {
        if (!active || !currentStep?.target) { setRect(null); setSearching(false); return; }

        let cancelled = false;
        setSearching(true);
        setRect(null);
        const startedAt = Date.now();

        function tryFind() {
            if (cancelled) return;
            const el = document.querySelector(currentStep.target);
            if (el) {
                setRect(el.getBoundingClientRect());
                setSearching(false);
                el.scrollIntoView({ block: 'center', behavior: 'smooth' });
                return;
            }
            if (Date.now() - startedAt > FIND_TIMEOUT_MS) {
                setSearching(false);
                return;
            }
            setTimeout(tryFind, FIND_POLL_MS);
        }
        tryFind();
        return () => { cancelled = true; };
    }, [active, currentStep]);

    // Keep the highlight glued to its target if the page scrolls/resizes
    // under it (sidebars especially can shift layout on hover/pin).
    useEffect(() => {
        if (!rect || !currentStep?.target) return;
        function reposition() {
            const el = document.querySelector(currentStep.target);
            if (el) setRect(el.getBoundingClientRect());
        }
        window.addEventListener('resize', reposition);
        window.addEventListener('scroll', reposition, true);
        return () => {
            window.removeEventListener('resize', reposition);
            window.removeEventListener('scroll', reposition, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rect, currentStep]);

    if (!active || !currentStep) return null;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const hasFrame = !!rect && !searching;

    // Card placement: below the highlighted element if there's room, else
    // above it; centered on screen when there's no target at all (a
    // domain-intro step, or a subpage step whose target never resolved).
    let cardStyle;
    if (hasFrame) {
        const spaceBelow = vh - rect.bottom;
        const placeBelow = spaceBelow > 220 || spaceBelow > rect.top;
        cardStyle = placeBelow
            ? { position: 'fixed', top: Math.min(rect.bottom + PAD * 2, vh - 260), left: Math.min(Math.max(rect.left, 16), vw - 380) }
            : { position: 'fixed', top: Math.max(rect.top - PAD * 2 - 240, 16), left: Math.min(Math.max(rect.left, 16), vw - 380) };
    } else {
        cardStyle = { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }

    return (
        <div ref={containerRef} style={{ position: 'fixed', inset: 0, zIndex: 1300 }}>
            {/* Dimmed backdrop — 4 bands framing the cutout when a target was
                found, or one full-screen scrim when centering a card. */}
            {hasFrame ? (
                <>
                    <div style={{ ...S.scrimBand, top: 0, left: 0, right: 0, height: Math.max(0, rect.top - PAD) }} />
                    <div style={{ ...S.scrimBand, top: rect.bottom + PAD, left: 0, right: 0, bottom: 0 }} />
                    <div style={{ ...S.scrimBand, top: rect.top - PAD, left: 0, width: Math.max(0, rect.left - PAD), height: rect.height + PAD * 2 }} />
                    <div style={{ ...S.scrimBand, top: rect.top - PAD, left: rect.right + PAD, right: 0, height: rect.height + PAD * 2 }} />
                    <motion.div
                        key={`ring-${currentIndex}`}
                        style={{
                            position: 'fixed', top: rect.top - PAD, left: rect.left - PAD,
                            width: rect.width + PAD * 2, height: rect.height + PAD * 2,
                            borderRadius: 12, border: '2.5px solid #6E58A6', pointerEvents: 'none',
                            boxShadow: '0 0 0 4px rgba(110,88,166,0.18)',
                        }}
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: [1, 1.02, 1] }}
                        transition={{ opacity: { duration: 0.2 }, scale: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } }}
                    />
                    {/* The "sparkle" — a small pulsing star at the frame's corner,
                        the "sparkling arrow" pointing at the real control. */}
                    <motion.div
                        style={{ position: 'fixed', top: rect.top - PAD - 14, left: rect.left - PAD - 14, fontSize: 22, pointerEvents: 'none' }}
                        animate={{ rotate: [0, 15, -10, 0], scale: [1, 1.25, 1] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                    >
                        ✨
                    </motion.div>
                </>
            ) : (
                <div style={{ ...S.scrimBand, inset: 0 }} />
            )}

            {/* The explanation card itself. */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentIndex}
                    style={{ ...S.card, ...cardStyle }}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.18 }}
                >
                    <div style={S.cardHeader}>
                        <span style={S.stepCounter}>Step {currentIndex + 1} of {steps.length}</span>
                        <button onClick={endTour} style={S.closeBtn} title="End tour">✕</button>
                    </div>
                    <div style={S.cardTitle}>{currentStep.title}</div>
                    <div style={S.cardDesc}>{currentStep.description}</div>
                    {currentStep.tip && <div style={S.tipBox}>💡 Try it: {currentStep.tip}</div>}
                    <div style={S.cardFooter}>
                        <button onClick={back} disabled={isFirst} style={{ ...S.backBtn, opacity: isFirst ? 0.4 : 1, cursor: isFirst ? 'default' : 'pointer' }}>← Back</button>
                        <button onClick={next} style={S.nextBtn}>{isLast ? 'Finish ✓' : 'Skip →'}</button>
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
}

const S = {
    scrimBand: { position: 'fixed', background: 'rgba(35,28,48,0.55)', transition: 'all 0.15s ease' },
    card: {
        width: 360, maxWidth: 'calc(100vw - 32px)', background: '#fff', borderRadius: 16,
        padding: '18px 20px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', fontFamily: "'Poppins', sans-serif",
        zIndex: 1301,
    },
    cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
    stepCounter: { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8C6FC0' },
    closeBtn: { border: 'none', background: 'transparent', color: '#9A90AE', fontSize: 14, cursor: 'pointer', lineHeight: 1, padding: 4 },
    cardTitle: { fontSize: 16.5, fontWeight: 700, color: '#231C30', marginBottom: 6 },
    cardDesc: { fontSize: 13, color: '#6B6379', lineHeight: 1.5 },
    tipBox: {
        marginTop: 10, fontSize: 12.5, color: '#4A3777', background: '#EEE9F8',
        padding: '9px 11px', borderRadius: 9, lineHeight: 1.45,
    },
    cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 },
    backBtn: { padding: '8px 14px', background: '#F5F2FA', color: '#6B6379', border: 'none', borderRadius: 9, fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit' },
    nextBtn: { padding: '8px 16px', background: '#6E58A6', color: '#fff', border: 'none', borderRadius: 9, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
};
